import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, Mic, MicOff, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { usePressTail } from '@/components/voice/usePressTail'
import { useSpeechRecognition } from '@/components/voice/useSpeechRecognition'

/**
 * Voice-to-text recorder for shop-floor dictation. Lives in two places:
 *
 *   1. Appointment edit dialog — Emil records a note from under the
 *      car; "Use raw" or "Summarize + use" pushes the text into the
 *      job-description textarea.
 *   2. Eric chat (Dashboard widget + Messaging continue dialog) via
 *      `mode="inline"` — the transcript flows into the chat textarea
 *      instead of staying inside this component's surface.
 *
 * Both surfaces use a **push-to-talk** mic button — hold to record,
 * release to stop. The toggle-style start/stop button was replaced
 * 2026-05-20 after Emil hit consistent Android Chrome dictation
 * dropouts: continuous-mode Web Speech sessions glitched mid-sentence
 * and produced garbled transcripts. Walkie-talkie style is
 * shorter-lived per press and commits the final segment cleanly on
 * release; multiple presses accumulate into the same transcript via
 * the underlying hook's `finalRef`.
 */

export type VoiceRecorderProps = {
  // What the parent does with the finalised text. The component leaves
  // its own internal transcript intact so the user can re-apply with a
  // different action without re-recording.
  onApply: (text: string) => void
  // Show a "Summarize with Eric" button alongside "Use raw text". The
  // summarized version hits POST /api/eric/summarize-note and replaces
  // the raw transcript in the apply path. Defaults to true; turn off
  // on surfaces where Claude polish is a category mistake (e.g. when
  // the transcript IS the user's prompt to Eric — Task #1).
  allowSummarize?: boolean
  // Mounting label override. Defaults to "Voice note" in i18n.
  headerKey?: string
  // After-apply behaviour: 'keep' (leave transcript in place; the user
  // can apply again) or 'clear' (reset on apply, since the parent
  // already absorbed the text). Default: 'keep' for appointment notes,
  // 'clear' is what Eric chat passes.
  applyMode?: 'keep' | 'clear'
}

export function VoiceRecorder({
  onApply,
  allowSummarize = true,
  headerKey,
  applyMode = 'keep',
}: VoiceRecorderProps) {
  const { t } = useTranslation('voice_recorder')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const speech = useSpeechRecognition()
  const [summary, setSummary] = useState<string | null>(null)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  // Tracks whether the user is actively pressing the mic. The guard is for
  // the START side: a second finger's pointerdown re-keys the press tail
  // silently (no release fires), and an unguarded speech.start() would
  // instantiate a second recogniser and orphan the live one — a hot mic
  // with no handle. The tail delivers release/cancel at most once, so the
  // stop side needs no extra idempotency.
  const pressedRef = useRef(false)

  // Re-sync after a stop the press didn't deliver (the 60s cap on a ghost
  // lost release, an error, a service self-end): the recogniser is down but
  // pressedRef would stay true and silently kill the NEXT press's start().
  useEffect(() => {
    if (speech.status !== 'listening') pressedRef.current = false
  }, [speech.status])

  const startListening = () => {
    if (pressedRef.current) return
    pressedRef.current = true
    speech.start()
  }
  const stopListening = () => {
    if (!pressedRef.current) return
    pressedRef.current = false
    speech.stop()
  }

  // Window-owned press tail (see usePressTail.ts — this button relabels
  // mid-press and Clear mounts as a sibling, the exact DOM-churn trap).
  // Cancel = stop-and-keep: the transcript is visible and editable here.
  const tail = usePressTail({
    onRelease: stopListening,
    onCancel: stopListening,
  })

  const summarize = useMutation({
    mutationFn: async (raw: string): Promise<string> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/eric/summarize-note', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: raw, kind: 'appointment_note' }),
      })
      if (!res.ok) {
        throw new Error(
          res.status === 429
            ? t('error.rate_limited')
            : t('error.summary_failed'),
        )
      }
      const data = (await res.json()) as { summary: string }
      return data.summary
    },
    onSuccess: (s) => {
      setSummary(s)
      setSummaryError(null)
    },
    onError: (err) => {
      setSummaryError(err instanceof Error ? err.message : String(err))
    },
  })

  const apply = (text: string) => {
    onApply(text)
    if (applyMode === 'clear') {
      speech.reset()
      setSummary(null)
      setSummaryError(null)
    }
  }

  if (speech.status === 'unsupported') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        {t('unsupported')}
      </div>
    )
  }

  const listening = speech.status === 'listening'
  const hasRaw = speech.transcript.trim().length > 0
  const liveText = speech.combined

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(headerKey ?? 'header')}
        </div>
        <div className="flex items-center gap-2">
          {hasRaw ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                speech.reset()
                setSummary(null)
                setSummaryError(null)
              }}
              className="h-7 px-2 text-xs"
            >
              {t('clear')}
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant={listening ? 'destructive' : 'default'}
            // Push-to-talk: pointer events unify mouse + touch + pen.
            // The press starts here; the release/cancel land on the
            // shared window tail above (capture + pointerId filtering
            // included). preventDefault stops Android Chrome from
            // synthesising a long-press context menu / text selection.
            onPointerDown={(e) => {
              e.preventDefault()
              tail.begin(e)
              startListening()
            }}
            // Block the right-click + long-press context menus that
            // would steal the press gesture on mobile Safari + Android;
            // the callout property is iOS Safari's variant of the trap.
            onContextMenu={(e) => e.preventDefault()}
            aria-pressed={listening}
            className="h-8 select-none touch-none [-webkit-touch-callout:none]"
          >
            {listening ? (
              <>
                <MicOff className="size-4" />
                {t('listening')}
              </>
            ) : (
              <>
                <Mic className="size-4" />
                {hasRaw ? t('hold_to_continue') : t('hold_to_talk')}
              </>
            )}
          </Button>
        </div>
      </div>

      {speech.status === 'denied' || speech.status === 'error' ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
          {speech.error}
        </div>
      ) : null}

      {/* Live transcript. Read-only so the recogniser owns the field
          — the user re-records or clears rather than editing typos in
          place. They CAN edit after applying. */}
      <Textarea
        value={liveText}
        readOnly
        rows={3}
        placeholder={t('placeholder_ptt')}
        className="bg-background"
      />

      {summary ? (
        <div className="space-y-1 rounded-md border border-brand/30 bg-brand-light/40 p-2">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
            <Sparkles className="size-3" />
            {t('summary_label')}
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{summary}</div>
        </div>
      ) : null}

      {summaryError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
          {summaryError}
        </div>
      ) : null}

      {hasRaw ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => apply(speech.transcript.trim())}
          >
            {t('use_raw')}
          </Button>
          {allowSummarize ? (
            summary ? (
              <Button
                type="button"
                size="sm"
                onClick={() => apply(summary)}
                className="bg-brand text-white hover:bg-brand-dark hover:text-white"
              >
                <Sparkles className="size-4" />
                {t('use_summary')}
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="default"
                onClick={() => {
                  setSummaryError(null)
                  summarize.mutate(speech.transcript.trim())
                }}
                disabled={summarize.isPending}
                className="bg-brand text-white hover:bg-brand-dark hover:text-white"
              >
                {summarize.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {t('summarizing')}
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4" />
                    {t('summarize')}
                  </>
                )}
              </Button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
